/** P6-style date: "14-Jul-26" (dd-Mon-yy). */
export function formatDate(d: string | null): string {
  if (!d) return "";
  // Anchor a bare "YYYY-MM-DD" to local midnight so a UTC parse doesn't render
  // the prior day in negative-offset zones.
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(d);
  const date = new Date(isDateOnly ? d + "T00:00:00" : d);
  if (Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = String(date.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
}

/** Compact numeric format: "4/13/26". For dense table layouts. */
export function formatDateNumeric(d: string | null): string {
  if (!d) return "";
  // Anchor a bare "YYYY-MM-DD" to local midnight (UTC parse renders the prior
  // day in negative-offset zones).
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(d);
  return new Date(isDateOnly ? d + "T00:00:00" : d).toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "2-digit",
  });
}

/** Month/day only: "4/13". For very dense chips/cells where the year is implied.
 *  Accepts both `"YYYY-MM-DD"` (treated as local) and full ISO timestamps. */
export function formatDateMonthDay(d: string | null): string {
  if (!d) return "";
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(d);
  const parsed = new Date(isDateOnly ? d + "T00:00:00" : d);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString(undefined, {
    month: "numeric",
    day: "numeric",
  });
}

/** "Apr 16" — abbreviated month + day, with the same date-only guard so a
 *  'YYYY-MM-DD' value never shifts a day under UTC parsing. Empty string when
 *  unset. */
export function formatDateMonthDayAbbrev(d: string | null): string {
  if (!d) return "";
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(d);
  const parsed = new Date(isDateOnly ? d + "T00:00:00" : d);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** "5m ago" / "2h ago" / "3d ago", falling back to "M/D" past a week. For
 *  feed-style listings of recent activity. */
export function formatRelativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return "just now";
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return formatDateMonthDay(iso);
}

/** Short timestamp like "May 16, 3:51 PM" — for log/feed-style lists. */
export function formatDateTime(d: string | null): string {
  if (!d) return "";
  return new Date(d).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDuration(d: number | null): string {
  if (d === null || d === undefined) return "";
  return `${Math.round(d)}d`;
}

export function statusLabel(s: string): string {
  switch (s) {
    case "TK_Active":
      return "Active";
    case "TK_NotStart":
      return "Not Started";
    case "TK_Complete":
      return "Complete";
    default:
      return s;
  }
}

export function statusBadgeClass(s: string): string {
  switch (s) {
    case "TK_Active":
      return "badge badge-active";
    case "TK_NotStart":
      return "badge badge-notstart";
    case "TK_Complete":
      return "badge badge-complete";
    default:
      return "badge";
  }
}

export function sectionStatusDot(status: string): string {
  switch (status) {
    case "complete":
      return "dot dot-complete";
    case "active":
      return "dot dot-active";
    default:
      return "dot dot-notstart";
  }
}

// total_float / free_float are stored in workdays (converted from P6 hours at
// ingest), so these helpers operate on days directly — no ÷8.
export function floatClass(totalFloat: number | null): string {
  if (totalFloat === null) return "";
  if (totalFloat <= 0) return "tf-critical";
  if (totalFloat <= 5) return "tf-near";
  return "tf-ok";
}

/** Trim a string to `max` characters, appending "…" when it's cut off. */
export function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max).trimEnd() + "…" : s;
}

export function floatDays(totalFloat: number | null): number | null {
  if (totalFloat === null) return null;
  return Math.round(totalFloat);
}

export function formatDataDate(raw: string | null): string | null {
  if (!raw) return null;
  // A bare "YYYY-MM-DD" is parsed as UTC midnight by Date(), which renders as
  // the PREVIOUS day in negative-offset zones — anchor it to local midnight so
  // a freshly-set (date-only) data date matches the reloaded (datetime) value.
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw);
  const d = new Date(isDateOnly ? raw + "T00:00:00" : raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}

/** Compact MM/DD/YY (2-digit year) — for dense controls like the schedule picker. */
export function formatDataDateShort(raw: string | null): string | null {
  if (!raw) return null;
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw);
  const d = new Date(isDateOnly ? raw + "T00:00:00" : raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
}

/** Signed workday delta for Δ chips: "+3d", "-2d", "0d". */
export function formatSignedDays(n: number): string {
  return `${n > 0 ? "+" : ""}${n}d`;
}
